import { StreamChat } from "stream-chat";

import { getClient, setClient } from "./client";
import {
  handleChannelEvent,
  handleClientEvent,
  loadChannelDMs,
  loadChannelRooms,
} from "./entities/channels";
import { flattenRawUser, updateUser } from "./entities/users";
import {
  channelsByChannelId,
  connectedAsUser,
  initializationState,
  messagesByChannelId,
  rawChannelsById,
  totalUnreadChannelCount,
  totalUnreadCount,
  usersById,
  usersTypingByChannelId,
} from "./state";

export function initializeClient(apiKey, settings) {
  const client = StreamChat.getInstance(apiKey, {
    recoverStateOnReconnect: false, // disable the 30 recover per doc recommendation and manually update channels
  });
  setClient(client, settings);
  client.on(handleClientEvent);
  return client;
}

export async function connectAsUser({ userId, communityCode, token }) {
  try {
    if (!userId || !token || !communityCode) {
      throw new Error("Cannot connect as user required arguments not provided");
    }
    initializationState.set((s) => ({ ...s, stage: "connecting" }));
    const rawUser = await getClient().connectUser(
      { id: userId, community: communityCode },
      token
    );
    connectedAsUser.set(rawUser.me);
    totalUnreadCount.set(rawUser.me.total_unread_count);
    totalUnreadChannelCount.set(rawUser.me.unread_channels);

    // Add currently logged in user to cache
    updateUser(flattenRawUser(rawUser.me));

    // Load users channels
    initializationState.set((s) => ({ ...s, stage: "loadingDms" }));
    await loadChannelDMs();
    initializationState.set((s) => ({ ...s, stage: "loadingRooms" }));
    await loadChannelRooms();

    initializationState.set({
      isInitialized: true,
      stage: "ready",
      error: null,
    });
  } catch (error) {
    initializationState.set((s) => ({ ...s, error }));
  }
}

// REMEMBER to update resetState() in /lib/connect.js if any entities are added or removed!
function resetState(error = null) {
  initializationState.set({
    isInitialized: false,
    stage: "preInit",
    error,
  });
  rawChannelsById.forEach((entry) => {
    if (entry.bound) {
      entry.rawChannel.off(handleChannelEvent);
      entry.bound = false;
    }
  });
  rawChannelsById.clear();

  connectedAsUser.set(null);
  totalUnreadCount.set(0);
  totalUnreadChannelCount.set(0);
  channelsByChannelId.set({});
  messagesByChannelId.set({});
  usersById.set({});
  usersTypingByChannelId.set({});
}

export async function disconnect() {
  initializationState.set((s) => ({ ...s, stage: "disconnecting" }));
  try {
    await getClient().disconnectUser();
    resetState(null);
  } catch (error) {
    resetState(error);
  }
}
